import type { LicensingIssueType } from './issuesV2';
import type { LicensingRuleType, Match, RevisionLicense } from './model-typings';
import type { LicenseTermId } from './types/licenses';
import type { OptionIncludableField } from './types/type-narrowers';

/**
 * Helper to determine if a licenseId is indicative of a custom license.
 * @param licenseId LicenseID / signature of the license to check
 * @returns Whether or not the license supports custom title / text
 */
export const isCustomLicense = (licenseId: string): boolean => {
  return ['custom-license', 'proprietary-license'].includes(licenseId);
};

const ruleTypeNames: Record<LicensingIssueType | LicensingRuleType, string> = {
  denied_license: 'denied license',
  flagged_license: 'flagged license',
  approved_license: 'approved license',
  unlicensed_dependency: 'unlicensed dependency',
  unconcluded_dependency: 'unconcluded dependency',
  multi_license_concluded: 'multi license concluded',
  policy_conflict: 'denied license',
  policy_flag: 'flagged license',
};

export const getRuleTypeName = (ruleType: LicensingIssueType | LicensingRuleType): string => {
  return ruleTypeNames[ruleType];
};

export type LicenseOptions = { includeLicenseText?: boolean; includeCopyright?: boolean; includeMatches?: boolean };

type IncludeLicenseTextField<Options extends LicenseOptions = LicenseOptions> = OptionIncludableField<
  Options['includeLicenseText'],
  'text',
  RevisionLicense['text']
>;
type IncludeCopyrightField<Options extends LicenseOptions = LicenseOptions> = OptionIncludableField<
  Options['includeCopyright'],
  'copyright',
  RevisionLicense['copyright']
>;
type IncludeMatchesField<Options extends LicenseOptions = LicenseOptions> = OptionIncludableField<
  Options['includeMatches'],
  'matches',
  Array<Pick<Match, 'id' | 'path' | 'copyrights' | 'ignored' | 'sources'>>
>;

export type License<
Options extends LicenseOptions = LicenseOptions,
> = {
  title: RevisionLicense['title'];
  licenseId: RevisionLicense['licenseId'];
  ignored: RevisionLicense['ignored'];
  licenseGroupId: number;
  revisionLicenseId: RevisionLicense['id'];
} & IncludeLicenseTextField<Options> & IncludeCopyrightField<Options> & IncludeMatchesField<Options>

/**
 * This matches the grouped license shape returned by DepsV1 and is needed for our issue scanners
 * @example
 * {
 *   licenseId: 'MIT',
 *   ignored: false,
 *   licenseGroupId: 1,
 *   group: {
 *     signature: 'MIT OR ISC',
 *     isDeclared: true,
 *     ignored: false,
 *   }
 * }
 */
export type GroupedLicense<
  Options extends LicenseOptions = LicenseOptions,
> = License<Options> & {
  group: { isDeclared: boolean; ignored: boolean; signature: string };
}

export type LicenseGroup<
  Options extends LicenseOptions = LicenseOptions,
> = {
  licenseGroupId: number;
  signature: string;
  ignored: boolean;
  isDeclared: boolean;
  licenses: Array<License<Options>>;
}

export const LicenseTermFallbackDescription: Record<LicenseTermId, string> = {
  'Include License': 'Including the full text of license in modified software.',
  'Commercial Use': 'Describes the ability to use the software for commercial purposes.',
  'Hold Liable': 'Describes the warranty and if the software/license owner can be charged for damages.',
  Modify: 'Describes the ability to modify the software and create derivatives.',
  Distribute: 'Describes the ability to distribute original or modified (derivative) works.',
  'Include Copyright': 'Describes whether the original copyright must be retained.',
  Sublicense: 'Describes the ability for you to grant/extend a license to the software.',
  'Private Use': 'Describes the ability to use/modify software freely without distributing it.',
  'Disclose Source': 'Describes whether you must disclose your source code when you distribute the software.',
  'State Changes': 'Stating significant changes made to software.',
  'Place Warranty': 'Describes the ability to place warranty on the software licensed.',
  'Include Original': 'Describes whether copies of the original software or instructions to obtain copies must be distributed with the software.',
  'Include Install Instructions': 'Describes the necessity to include build & install instructions.',
  'Use Patent Claims': 'Describes the rights to practice patent claims of contributors to the code.',
  'Include Notice': 'Include a "NOTICE" file with attribution notes included.',
  Rename: 'Describes the ability to change software\'s name if modified/distributed.',
  'Use Trademark': 'Describes the allowance of using contributors\' names, trademarks or logos.',
  'Contact Author': 'Describes your ability to contact the author about the module you\'re using.',
  Relicense: 'Describes the ability to freely relicense the software.',
  'Give Credit': 'Describes the need to give explicit credit to the author when distributing the software.',
  'Statically Link': 'Describes whether the library can be compiled into the program (linked at compile time rather than runtime, aka dynamic linking).',
  'Compensate for Damages': 'Describes the obligation to compensate the author for any damages cased by your work.',
  'Pay Above Use Threshold': 'Describes the obligation to pay the licensor after a certain amount of use.',
};
